import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import Dropzone from "react-dropzone";
import { Link } from "react-router-dom";
import { Input, Row, Col, Card, CardBody, Button, Spinner } from "reactstrap"; // Added Spinner

import { uploadCategoryIcon, uploadDarkIcon } from "../../../slices/thunks";
import { useDispatch, useSelector } from "react-redux";
import { toast } from "react-toastify";

const defaultColor = "#000000"; // Default color for the color picker

const HumanCategoriesForm = ({ labels, formikProps, onTranslateClickHandler, translationLoading, lastTranslatedField, isAddOrEdit }) => {
    const { t, i18n } = useTranslation();
    const dispatch = useDispatch();
    const isRTL = i18n.dir() === 'rtl'
    const today = new Date().toISOString().split("T")[0];
    const [filePreviews, setFilePreviews] = useState({});


    const { ImageLoading, error , uploadedImage, DarkImageLoading, uploadedDarkImage } = useSelector(
      (state) => ({
        ImageLoading: state.HumanCategories.ImageLoading,
        error: state.HumanCategories.error,
        uploadedImage: state.HumanCategories.uploadedImage,
        DarkImageLoading: state.HumanCategories.DarkImageLoading,
        uploadedDarkImage: state.HumanCategories.uploadedDarkImage,
      })
    );
    

  // Destructure from formikProps for easier access
  const { values, errors, touched, handleChange, handleBlur, setFieldValue } = formikProps;

  const handleFileDrop = (acceptedFiles, fieldName) => {
    if (acceptedFiles.length === 0) return;

    const file = acceptedFiles[0];

    // Set file value to Formik
    setFieldValue(fieldName, file);

    const reader = new FileReader();
    reader.readAsDataURL(file);

    reader.onloadend = () => {
      let base64 = reader.result.replace(/^data:image\/(png|jpeg);base64,/, '');

      if (fieldName === "LightIcon") {
        dispatch(uploadCategoryIcon({ ImageData: base64 }));
      }

      if (fieldName === "DarkIcon") {
        dispatch(uploadDarkIcon({ ImageData: base64 }));
      }
    };
  };

  useEffect(() => {
    if (uploadedImage && !ImageLoading) {
      setFieldValue('LightIcon', uploadedImage);
    }
  }, [uploadedImage, ImageLoading]);

  useEffect(() => {
    if (uploadedDarkImage && !DarkImageLoading) {
      setFieldValue('DarkIcon', uploadedDarkImage);
    }
  }, [uploadedDarkImage, DarkImageLoading]);
  
  // Helper function to format file sizes
  function formatBytes(bytes, decimals = 2) {
      if (!+bytes) return '0 Bytes';
      const k = 1024;
      const dm = decimals < 0 ? 0 : decimals;
      const sizes = ['Bytes', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB'];
      const i = Math.floor(Math.log(bytes) / Math.log(k));
      return `${parseFloat((bytes / Math.pow(k, i)).toFixed(dm))} ${sizes[i]}`;
  }
    

  const renderField = (item) => {
    const isDisabled = typeof item.disabled === 'function' ? item.disabled(isAddOrEdit) : !!item.disabled;
    const fieldValue = values[item.name] ?? "";
    const fieldName = item.name;

    switch (item.fieldName) {
      case "text-box":
        return (
            <Input
              type={"text"}
              id={item.name}
              name={item.name}
              placeholder={t(item.placeholder || item.labelName)}
              value={fieldValue}
              onChange={(e) => {
              const inputValue = e.target.value;
              
              // If it's RoleName field, allow only letters and spaces
              if (item.name === "ScreenNameAr" ) {
                const cleanedValue = inputValue.replace(/[^A-Za-z\u0600-\u06FF\s]/g, '');
                setFieldValue(item.name, cleanedValue);
              } else {
                handleChange(e);
              }
              }}
              maxLength={item.maxLength} 
				      minLength={item.minLength}
              onBlur={handleBlur}
              className="form-control"
              invalid={touched[item.name] && !!errors[item.name]}
              disabled={isDisabled}
              required={item.required}
            />
        );
    
      case "email-box":
        return (
          <Input
            type={"email"}
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control"
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case "number":
        return (
          <Input
            type="tel"
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue}
            onChange={handleChange} 
            onBlur={handleBlur}
            className="form-control"
            // maxLength={maxLength}
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case 'number-box':
        return (
            <Input
                type="text"
                style={{ textAlign: isRTL ? 'right' : 'left' }}
                id={item.name}
                name={item.name}
                placeholder={t(item.placeholder || item.labelName)}
                value={fieldValue}
                onChange={(e) => {
                    const inputValue = e.target.value;
                    let filteredValue = inputValue.replace(/[^0-9.]/g, '');

                    const parts = filteredValue.split('.');
                    if (parts.length > 2) {
                        filteredValue = parts[0] + '.' + parts.slice(1).join('');
                    }
                    setFieldValue(item.name, filteredValue);
                }}
                onBlur={handleBlur}
                className="form-control"
                invalid={touched[item.name] && !!errors[item.name]}
                disabled={isDisabled}
                required={item.required}
            />
        );


      case "text-box-translate":
        return (
          <div className="input-group"> 
            <Input
              type={"text"}
              id={item.name}
              name={item.name}
              placeholder={t(item.placeholder || item.labelName)}
              value={fieldValue}
              onChange={(e) => {
              const inputValue = e.target.value;
              
              // If it's RoleName field, allow only letters and spaces
              if (item.name === "ScreenNameEn" ) {
                const cleanedValue = inputValue.replace(/[^A-Za-z\s]/g, '');
                setFieldValue(item.name, cleanedValue);
              } else {
                handleChange(e);
              }
              }}             
              maxLength={item.maxLength} 
				      minLength={item.minLength}
              onBlur={handleBlur}
              className="form-control"
              invalid={touched[item.name] && !!errors[item.name]}
              required={item.required}
            />
            {onTranslateClickHandler && item.translateTo && (
              <Button
                type="button"
                onClick={() => onTranslateClickHandler(item.translateTo, fieldValue, setFieldValue, item.name)} // Pass item.name as sourceFieldName
                disabled={!fieldValue || !fieldValue.trim() || translationLoading} // Disable if any translation is loading
              >
                {translationLoading && lastTranslatedField === item.name ? (
                  <Spinner size="sm" />
                ) : (
                  <i className="ri-translate"></i>
                )}
              </Button>
            )}
          </div>
        );

      case "color-picker": // Added case for color picker
        return (
          <Input
            type="color"
            id={item.name}
            name={item.name}
            placeholder={t(item.placeholder || item.labelName)}
            value={fieldValue || defaultColor} // Use defaultColor if value is empty
            onChange={handleChange}
            onBlur={handleBlur}
            className="form-control form-control-color" // Use form-control-color class for styling
            invalid={touched[item.name] && !!errors[item.name]}
            required={item.required}
          />
        );

      case 'icon':
        let previewSrc = null;
        let uploadedFileName = '';
        let uploadedFileSize = '';
        const currentFile = values[fieldName];

        if (typeof currentFile === 'string' && currentFile) { // Existing file URL
            previewSrc = currentFile;
            uploadedFileName = currentFile.substring(currentFile.lastIndexOf('/') + 1);
        } else if (currentFile instanceof File) { // New file
            previewSrc = filePreviews[fieldName]; 
            uploadedFileName = currentFile.name;
            uploadedFileSize = formatBytes(currentFile.size);
        }

        return (
            <CardBody className="p-0"> 
                <Dropzone
                    onDrop={(acceptedFiles, fileRejections) => {
                      // Only allow PNG/JPG/JPEG and max 5MB
                      if (fileRejections && fileRejections.length > 0) {
                          const isTypeError = fileRejections.some(rej =>
                              rej.errors.some(err => err.code === "file-invalid-type")
                          );
                          const isSizeError = fileRejections.some(rej =>
                              rej.errors.some(err => err.code === "file-too-large")
                          );
                          if (isTypeError) {
                              toast.error(t("Only PNG and JPG images are allowed"));
                          } else if (isSizeError) {
                              toast.error(t("Image size must be 5 MB or less"));
                          } else {
                              toast.error(t("Invalid file"));
                          }
                          return;
                      }
                      handleFileDrop(acceptedFiles, fieldName);
                    }}              
                    disabled={isDisabled || ((ImageLoading && fieldName == 'LightIcon') || (DarkImageLoading && fieldName == 'DarkIcon'))}
                    accept={item.accept}
                >
                    {({ getRootProps, getInputProps }) => (
                        <div
                            className="dropzone dz-clickable"
                            style={{ minHeight: '60px', position: 'relative' }}
                        >
                            <div className="dz-message needsclick" {...getRootProps()}>
                                <input {...getInputProps()} />
                                {((ImageLoading && fieldName == 'LightIcon') || (DarkImageLoading && fieldName == 'DarkIcon')) && (
                                    <div style={{ position: 'absolute', top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 10, pointerEvents: 'none' }}>
                                        <Spinner size="lg" color="primary" />
                                    </div>
                                )}
                                <div style={{ opacity: (ImageLoading && fieldName == 'LightIcon') || (DarkImageLoading && fieldName == 'DarkIcon') ? 0.5 : 1 }}>
                                    <h5>{t("DropFile")}</h5>
                                </div>
                            </div>
                        </div>
                    )}
                </Dropzone>

                {previewSrc && (
                    <Card className="mt-1 mb-0 shadow-none border dz-processing dz-image-preview dz-success dz-complete">
                        <div className="p-2">
                            <Row className="align-items-center">
                                <Col className="col-auto">
                                    <img
                                        data-dz-thumbnail=""
                                        height="80"
                                        className="avatar-sm rounded bg-light"
                                        src={previewSrc}
                                        alt={uploadedFileName || "preview"}
                                    />
                                </Col>
                                <Col>
                                    <Link to="#" className="text-muted font-weight-bold">
                                        {uploadedFileName}
                                    </Link>
                                    {uploadedFileSize && <p className="mb-0"><strong>{uploadedFileSize}</strong></p>}
                                </Col>
                                {/* Optional: Button to clear the file */}
                                {!isDisabled && (values[fieldName]) && (
                                    <Col className="col-auto">
                                        <Button
                                            color="danger"
                                            size="sm"
                                            onClick={() => {
                                                setFieldValue(fieldName, null); 
                                                setFilePreviews(prev => {
                                                    if (prev[fieldName] && prev[fieldName].startsWith('blob:')) {
                                                        URL.revokeObjectURL(prev[fieldName]);
                                                    }
                                                    const newState = {...prev};
                                                    delete newState[fieldName];
                                                    return newState;
                                                });
                                            }}
                                        >
                                            <i className="ri-close-line"></i>
                                        </Button>
                                    </Col>
                                )}
                            </Row>
                        </div>
                    </Card>
                )}
            </CardBody>
        );
     
      default:
        return null;
    }
  };

  return (
    <Row>
      {labels.map((item) => {
        
        return (
          <Col sm={6} key={item.name}>
            <div className="mb-3">
              {item.labelName !== "Mobile" && (
                <label htmlFor={item.name} className="col-form-label">
                  {t(item.labelName)}
                  {item.required && <span className="text-danger"> *</span>}
                </label>
              )} 
              {/* Render the field */}
              {renderField(item)}
              {/* Display error for the main field */}
              {item.labelName !== "Mobile" && touched[item.name] && errors[item.name] &&(
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(errors[item.name])}</div>
              )}
              {item.fieldName === "icon" && touched[item.name] && errors[item.name] && (
                <div className="text-danger mt-1" style={{ fontSize: "0.875rem" }}>{t(errors[item.name])}</div>
              )}
            </div>
          </Col>
        );
      })}
    </Row>
  );
}

export default HumanCategoriesForm